import React, { memo } from "react";
import { getCatalogEntry, getCategoryLabel, snapQuantityForEntry } from "@/data/vipSimulatorCatalog";
import { isArmorClothingEntry, isOutfitEntry } from "@/data/vipClothingRules";
import LazyCatalogImage from "./LazyCatalogImage";
import QuantityStepper from "./QuantityStepper";
import { useI18n } from "@/i18n/I18nProvider";
import {
    formatBRL,
    formatQty,
    formatSlotQuantity,
    formatUnitBRL,
    lineSubtotal,
    unitPriceForKey,
} from "./vipSimulatorUtils";

function ItemCard({
    placement,
    inPool,
    onQuantityChange,
    onRemoveFromKit,
    onAddToKit,
}) {
    const { t, item, lang } = useI18n();
    const entry = getCatalogEntry(placement.catalogId);
    if (!entry) return null;
    const displayName = item(entry.name);

    const qEn = !!entry.quantityEnabled;
    const canEditQuantity = inPool && qEn;
    const unit = unitPriceForKey(entry.priceKey);
    const qty = qEn ? placement.quantity : 1;
    const sub = lineSubtotal(unit, qty);
    const catKey = entry.category || "misc";
    const isArmorSlot = isArmorClothingEntry(entry);
    const isOutfit = isOutfitEntry(entry);

    const handlePoolClick = () => {
        if (inPool && onAddToKit) onAddToKit(placement.instanceId);
    };

    const handleKeyDown = (e) => {
        if (!inPool || !onAddToKit) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onAddToKit(placement.instanceId);
        }
    };

    return (
        <div
            className={`vip-sim-item ${inPool ? "vip-sim-item--pool vip-sim-item--clickable" : ""}`}
            role={inPool ? "button" : undefined}
            tabIndex={inPool ? 0 : undefined}
            onClick={inPool ? handlePoolClick : undefined}
            onKeyDown={inPool ? handleKeyDown : undefined}
            title={
                inPool
                    ? entry.quantityEnabled
                        ? t("simulator.item.addTitleQty", { name: displayName })
                        : t("simulator.item.addTitle", { name: displayName })
                    : displayName
            }
        >
            <span className="vip-sim-item__badge">{getCategoryLabel(catKey)}</span>
            <div className="vip-sim-item__img-wrap">
                {entry.icon ? (
                    inPool ? (
                        <LazyCatalogImage
                            className="vip-sim-item__img"
                            src={entry.icon}
                            fallbacks={entry.iconCandidates}
                            alt=""
                        />
                    ) : (
                        <img className="vip-sim-item__img" src={entry.icon} alt="" draggable={false} loading="lazy" />
                    )
                ) : entry.emoji ? (
                    <span className="vip-sim-item__emoji">{entry.emoji}</span>
                ) : null}
                {!inPool ? (
                    <span className="vip-sim-item__slot-qty">x{formatSlotQuantity(qty, lang)}</span>
                ) : null}
            </div>
            <div className="vip-sim-item__name">{displayName}</div>
            <div className="vip-sim-item__meta">
                <div className="vip-sim-item__unit">
                    {unit > 0
                        ? t("simulator.item.unit", { price: formatUnitBRL(unit, lang) })
                        : t("simulator.item.noPrice")}
                </div>
                {canEditQuantity ? (
                    <QuantityStepper
                        value={placement.quantity}
                        step={entry.step ?? 1}
                        min={entry.min ?? 1}
                        max={entry.max ?? 99999999}
                        disabled={false}
                        onChange={(v) =>
                            onQuantityChange(placement.instanceId, snapQuantityForEntry(entry, v))
                        }
                    />
                ) : (
                    <div className="vip-sim-item__qty-note">
                        {isArmorSlot
                            ? t("simulator.item.wearable")
                            : isOutfit
                              ? t("simulator.item.outfitNote")
                              : t("simulator.item.qty", { qty: formatQty(qty, lang) })}
                    </div>
                )}
                <div className="vip-sim-item__sub">{t("simulator.item.sub", { amount: formatBRL(sub, lang) })}</div>
            </div>
            {!inPool && onRemoveFromKit && (
                <button
                    type="button"
                    className="vip-sim-item__remove"
                    aria-label={t("simulator.item.remove")}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemoveFromKit(placement.instanceId);
                    }}
                >
                    ×
                </button>
            )}
        </div>
    );
}

export default memo(ItemCard, (prev, next) => {
    return (
        prev.inPool === next.inPool &&
        prev.placement.instanceId === next.placement.instanceId &&
        prev.placement.quantity === next.placement.quantity &&
        prev.placement.catalogId === next.placement.catalogId
    );
});
